import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';
import { lowerCase, startCase } from 'lodash';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { DatePicker } from 'app/components/elements/DatePicker';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { dropdownValueTypes } from 'const/option';
import { entriesInclude } from 'utils/entries';
import { getNameOption } from 'utils/convertData';
import { formatAppDateTime } from 'utils/dateFormat';
import type { RoomIssueFormValues } from '../hooks/useRoomIssueDialog';

export const RoomIssueLegacyFields = ({
  form,
  isEditing
}: {
  form: UseFormReturn<RoomIssueFormValues>;
  isEditing: boolean;
}) => {
  const { systemDropdownValues, singleEntryField, users } = useFilteredRoomContext();
  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const roomLevelIssueOptions = useMemo(
    () =>
      systemDropdownValues?.filter((item) =>
        item.options.some((option) => option.type === dropdownValueTypes.roomLevelIssue)
      ) ?? [],
    [systemDropdownValues]
  );

  const roomLevelIssueSingleFields = useMemo(
    () =>
      singleEntryField?.filter((item) =>
        entriesInclude(item.entries, dropdownValueTypes.roomLevelIssue)
      ) ?? [],
    [singleEntryField]
  );

  const readOnly = (label: string, value?: string | null) => (
    <Stack gap={0.5}>
      <Typography fontWeight={700}>{label}</Typography>
      <Typography whiteSpace="pre-line">{value || '-'}</Typography>
    </Stack>
  );

  if (!isEditing) {
    return (
      <Stack gap={2}>
        {roomLevelIssueOptions.map((opt) => {
          const val = watch(`option_${opt.id}`);
          const otherVal = watch(`option_${opt.id}_name`);
          const display = val === 'OTHER' ? String(otherVal || '') : String(val || '');
          return (
            <Stack key={opt.id} gap={0.5}>
              <Typography fontWeight={700}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {startCase(lowerCase(getNameOption(opt as any)))}
              </Typography>
              <Typography>{display || '-'}</Typography>
            </Stack>
          );
        })}
        {roomLevelIssueSingleFields.map((field) =>
          readOnly(field.name, String(watch(`single_${field.id}`) || ''))
        )}
        {readOnly('Description', watch('description'))}
        {readOnly('Status', watch('status'))}
        {watch('status') === 'RESOLVED' &&
          readOnly(
            'Resolution Date',
            watch('resolutionDate') ? formatAppDateTime(watch('resolutionDate')) : '-'
          )}
        {readOnly('Action By', watch('actionBy'))}
        {readOnly(
          'Action By Date',
          watch('actionByDate') ? dayjs(watch('actionByDate')).format('YYYY-MM-DD') : '-'
        )}
        {readOnly('Validated', watch('validated'))}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {roomLevelIssueOptions.map((opt) => (
        <React.Fragment key={opt.id}>
          <Controller
            control={control}
            name={`option_${opt.id}`}
            render={({ field: { onChange, value }, fieldState }) => (
              <InputSelect
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                inputError={fieldState.error?.message}
                value={value as string}
                placeholder="Select Issue Type"
                onChange={onChange}
                options={opt.dropdownValues?.map((item) => ({
                  label: item.value,
                  value: item.value
                }))}
              />
            )}
          />
          {watch(`option_${opt.id}`) === 'OTHER' && (
            <Controller
              name={`option_${opt.id}_name`}
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  title="Other Issue"
                  placeholder="Enter Other Issue"
                  inputError={fieldState.error?.message}
                  inputProps={{ ...field }}
                />
              )}
            />
          )}
        </React.Fragment>
      ))}
      {roomLevelIssueSingleFields.map((field) => (
        <Stack style={{ width: '100%' }} key={field.id}>
          {field.type === 'DATE' ? (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f }) => (
                <DatePicker
                  title={`${field.name}:`}
                  onChange={(val) =>
                    setValue(`single_${field.id}`, val ? dayjs(val).format('YYYY-MM-DD') : '')
                  }
                  value={f.value ? dayjs(f.value as string) : undefined}
                />
              )}
            />
          ) : (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f, fieldState }) => (
                <InputText
                  title={`${field.name}:`}
                  placeholder={`Enter ${field.name}`}
                  inputProps={{ ...f }}
                  inputError={fieldState.error?.message}
                />
              )}
            />
          )}
        </Stack>
      ))}
      <Controller
        name="description"
        control={control}
        render={({ field, fieldState }) => (
          <InputText
            title="Description"
            placeholder="Enter Description"
            inputError={fieldState.error?.message}
            inputProps={{
              ...field,
              multiline: true,
              rows: 4,
              style: { padding: 0 }
            }}
          />
        )}
      />
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <InputSelect
            title="Status"
            inputError={errors.status?.message || ''}
            value={value as string}
            placeholder="Status"
            onChange={(e: SelectChangeEvent<unknown>) => {
              onChange(e.target.value);
              if (e.target.value === 'RESOLVED') {
                setValue('resolutionDate', dayjs().toISOString());
              }
            }}
            options={[
              { value: 'ACTIVE', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' }
            ]}
          />
        )}
        name="status"
      />
      {watch('status') === 'RESOLVED' && (
        <Controller
          name="resolutionDate"
          control={control}
          render={({ field: f }) => (
            <DatePicker
              includeTime
              title="Resolution date"
              onChange={(val) => setValue('resolutionDate', val ? dayjs(val).toISOString() : '')}
              value={f.value ? dayjs(f.value as string) : undefined}
            />
          )}
        />
      )}
      <Controller
        name="actionBy"
        control={control}
        render={({ field, fieldState }) => (
          <InputSelect
            title="Action By:"
            placeholder="Select User"
            value={field.value as string}
            inputError={fieldState.error?.message}
            onChange={field.onChange}
            options={users?.map((item) => ({ label: item.label, value: item.label })) ?? []}
          />
        )}
      />
      <Controller
        control={control}
        render={({ field: { value } }) => (
          <DatePicker
            inputError={errors.actionByDate?.message}
            style={{ flexDirection: 'column' }}
            title="Choose Action By Date"
            onChange={(val) => setValue('actionByDate', val ? dayjs(val).toISOString() : '')}
            value={value ? dayjs(value as string) : undefined}
          />
        )}
        name="actionByDate"
      />
      <Controller
        name="validated"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <InputSelect
            title="Validated"
            inputError={fieldState.error?.message}
            value={value as string}
            placeholder="Validated"
            onChange={onChange}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' }
            ]}
          />
        )}
      />
    </Stack>
  );
};
